import {
    MESSAGES,
    PERSISTENT_NOTIFICATION_COMETD_KEY
} from '$src/lib/services/cometd/cometd.constants';
import type { TSessionEndedReason } from '@casumo/fabric-fundamentals';
import type { TCreateEventConverterProps } from './eventConverter.types';

export const walletEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'wallet-balance-updated',
        eventKey: 'walletBalanceUpdated',
        // TODO: Fix source type here
        convert: (data) => ({ source: data.walletBalanceUpdated?.source.toLowerCase() as any }) // eslint-disable-line @typescript-eslint/no-explicit-any
    },
    {
        kind: 'enriched-deposit-confirmed',
        eventKey: 'enrichedDepositConfirmed'
    },
    {
        kind: 'deposit-failed',
        eventKey: 'depositFailed'
    },
    {
        kind: 'money-limit-change-scheduled',
        eventKey: 'moneyLimitChangeScheduled'
    },
    {
        kind: 'money-limit-revoked',
        eventKey: 'moneyLimitRevoked'
    },
    {
        kind: 'money-limit-set',
        eventKey: 'moneyLimitSet'
    },
    {
        kind: 'player-payments-permissions-updated',
        eventKey: 'playerPaymentsPermissionsUpdated'
    },
    {
        kind: 'payment-transaction-created',
        eventKey: 'paymentTransactionCreated'
    },
    {
        kind: 'payment-transaction-rolled-back',
        eventKey: 'paymentTransactionRolledBack'
    },
    {
        kind: 'payment-transaction-updated',
        eventKey: 'paymentTransactionUpdated'
    },
    {
        kind: 'withdrawal-locked',
        eventKey: 'withdrawalLocked'
    }
];

export const playerValuablesEventConverterConfig: TCreateEventConverterProps[] = [
    {
        kind: 'item-created',
        eventKey: 'notificationAdded',
        isEvent: (event) => event.data?.notificationAdded?.type === 'ITEM_EARNED'
    }
];

export const adventurerValuablesEventConverterConfig: TCreateEventConverterProps[] = [
    {
        kind: 'item-state-changed',
        eventKey: 'itemStateChanged',
        convert: (data) => ({
            badgeId: data?.itemStateChanged?.badgeId,
            state: data?.itemStateChanged?.state
        })
    },
    {
        kind: 'item-state-changed',
        eventKey: 'itemCreated',
        convert: (data) => ({
            badgeId: data?.itemCreated?.event?.badgeId,
            state: 'Fresh'
        })
    }
];

export const mandatoryMessagesEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'mandatory-messages',
        eventKey: 'mandatoryMessageReceived',
        convert: (data) => data?.mandatoryMessageReceived?.message
    }
];

export const sessionConfigurationEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'es-session-stats-updated',
        eventKey: MESSAGES.ES_SESSION_STATUS_UPDATED,
        convert: (data) => ({
            payload: data[MESSAGES.ES_SESSION_STATUS_UPDATED]
        })
    },
    {
        kind: 'es-session-ended',
        eventKey: MESSAGES.ES_SESSION_ENDED,
        convert: (data) => {
            const payload = data[MESSAGES.ES_SESSION_ENDED];
            return {
                payload: {
                    lastUpdateTime: Date.now(),
                    activeSession: payload.activeSession,
                    lastEndedSession: payload.lastEndedSession,
                    activeExclusion: payload.activeExclusion
                }
            };
        }
    },
    {
        kind: 'es-session-limit-almost-consumed',
        eventKey: MESSAGES.ES_SESSION_LIMIT_ALMOST_CONSUMED,
        convert: (data) => ({
            payload: data[MESSAGES.ES_SESSION_LIMIT_ALMOST_CONSUMED]
        })
    },
    {
        kind: 'es-session-periodic-reminder',
        eventKey: MESSAGES.ES_SESSION_PERIODIC_REMINDER,
        convert: (data) => {
            const payload = data[MESSAGES.ES_SESSION_PERIODIC_REMINDER];
            return {
                payload: {
                    remainingTimeInSeconds: payload.remainingTimeInSeconds,
                    session: payload.session,
                    timestamp: payload.timestamp
                }
            };
        }
    },
    {
        kind: 'es-session-time-remaining',
        eventKey: MESSAGES.ES_SESSION_TIME_REMAINING,
        convert: (data) => {
            const payload = data[MESSAGES.ES_SESSION_TIME_REMAINING];
            return {
                payload: {
                    remainingTimeInSeconds: payload.remainingTimeInSeconds,
                    session: payload.session,
                    timestamp: payload.timestamp
                }
            };
        }
    }
];

export const gameSessionNetFinancialPositionUpdatedConverterConfigs: TCreateEventConverterProps[] =
    [
        {
            kind: 'net-financial-position-updated',
            eventKey: 'gameSessionNetFinancialPositionChanged',
            convert: (data) => {
                const payload = data?.gameSessionNetFinancialPositionChanged;
                return {
                    payload: {
                        totalRealBetAmount: payload.totalRealBetAmount.amount,
                        totalRealWinAmount: payload.totalRealWinAmount.amount
                    }
                };
            }
        }
    ];

export const umoboardEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'status-change',
        eventKey: 'leaderboardId',
        isEvent: (event) => Boolean(event?.data?.leaderboardId) && Boolean(event?.data?.status),
        convert: (data) => {
            return {
                data
            };
        }
    }
];

export const realityCheckEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'reality-check',
        eventKey: 'realityCheck',
        convert: (data) => data?.realityCheck
    }
];

export const sessionEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'session-ended',
        eventKey: 'data',
        isEvent: (event) => /^\/session\/[^/]+\/ended$/.test(event.channel), // e.g match /session/123/ended
        convert: (data) => ({ reason: data as unknown as TSessionEndedReason })
    }
];

export const persistentNotificationsEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'persistent-notification-created',
        eventKey: PERSISTENT_NOTIFICATION_COMETD_KEY,
        isEvent: (event) => Boolean(event?.data?.[PERSISTENT_NOTIFICATION_COMETD_KEY]),
        convert: (data) => {
            const payload = data[PERSISTENT_NOTIFICATION_COMETD_KEY];
            return {
                id: payload.notificationId,
                data: payload.data
            };
        }
    }
];

export const kycEventConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'kyc-status-update',
        eventKey: 'kycVerificationItemStateSet'
    }
];

export const depositLimitAdjustmentEventConverterConfig: TCreateEventConverterProps[] = [
    {
        kind: 'deposit-limit-adjustment',
        eventKey: 'notificationAdded',
        isEvent: (event) =>
            event.data?.notificationAdded?.type === 'DGOJ_DEPOSIT_LIMIT_ADJUSTMENT_APPROVED' ||
            event.data?.notificationAdded?.type === 'DGOJ_DEPOSIT_LIMIT_ADJUSTMENT_REJECTED',
        convert: (data) => ({ type: data?.notificationAdded?.type })
    }
];

export const sportsEventsConverterConfig: TCreateEventConverterProps[] = [
    {
        kind: 'sports-notification',
        eventKey: 'notificationAdded',
        isEvent: (event) =>
            event.data?.notificationAdded?.type ===
                'bet_not_accepted_due_to_bonus_money_in_wallet' ||
            event.data?.notificationAdded?.type ===
                'canada_sports_player_bet_not_accepted_geolocation',
        convert: (data) => ({ type: data?.notificationAdded?.type })
    }
];

export const kycNotificationConverterConfigs: TCreateEventConverterProps[] = [
    {
        kind: 'kyc-notification',
        eventKey: 'notificationAdded',
        isEvent: (event) =>
            ['KYC_PROCESS_STARTED', 'KYC_PROCESS_UPDATED', 'KYC_PROCESS_FINISHED'].includes(
                event.data?.notificationAdded?.type
            ),
        convert: (data) => ({ type: data?.notificationAdded?.type })
    }
];
